import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Collapse,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Assignment as AssignmentIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PolicyReviewPage = () => {
  const navigate = useNavigate();
  const [isGuideExpanded, setIsGuideExpanded] = useState(false);
  
  // 修改模拟数据，移除 priority 字段
  const policies = [
    {
      id: 1,
      policyNumber: 'POL-2024-001',
      type: 'Auto Insurance',
      submittedBy: 'John Smith',
      submittedDate: '2024-01-25',
      complexity: 'medium',
      status: 'pending_review'
    },
    {
      id: 2,
      policyNumber: 'POL-2024-002',
      type: 'Home Insurance',
      submittedBy: 'Emma Wilson',
      submittedDate: '2024-01-24',
      complexity: 'low',
      status: 'in_review'
    },
    {
      id: 3,
      policyNumber: 'POL-2024-003',
      type: 'Life Insurance',
      submittedBy: 'Michael Brown',
      submittedDate: '2024-01-23',
      complexity: 'high',
      status: 'pending_review'
    }
  ];

  // 添加已批准的保单数据
  const approvedPolicies = [
    {
      id: 4,
      policyNumber: 'POL-2024-004',
      type: 'Auto Insurance',
      submittedBy: 'David Lee',
      submittedDate: '2024-01-22',
      complexity: 'low',
      status: 'approved',
      approvedDate: '2024-01-24'
    },
    // ... 更多已批准的保单
  ];

  // 修改难度等级的配置
  const complexityLevels = {
    low: {
      icon: <CheckCircleIcon fontSize="small" />,
      color: 'success',
      label: 'Simple',
      description: '• No accidents in past 6 years\n• No traffic tickets\n• No out-of-province driving experience'
    },
    medium: {
      icon: <WarningIcon fontSize="small" />,
      color: 'warning',
      label: 'Moderate',
      description: '• Has accidents or traffic tickets\n• No out-of-province driving experience\n• Standard documentation'
    },
    high: {
      icon: <ErrorIcon fontSize="small" />,
      color: 'error',
      label: 'Complex',
      description: '• Multiple accidents or tickets\n• Out-of-province driving experience\n• Letter of Experience (LOE) required\n• Complex documentation review needed'
    }
  };

  // 获取复杂度标签
  const getComplexityChip = (complexity) => {
    const config = complexityLevels[complexity];
    return (
      <Tooltip title={
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2">{config.label} Case:</Typography>
          <ul style={{ paddingLeft: '20px', margin: '8px 0 0 0' }}>
            {config.description.split('\n').map((line, index) => (
              <li key={index} style={{ fontSize: '0.875rem' }}>{line.replace('• ', '')}</li>
            ))}
          </ul>
        </Box>
      }>
        <Chip
          icon={config.icon}
          label={config.label}
          size="small"
          color={config.color}
          variant="outlined"
        />
      </Tooltip>
    );
  };

  // 获取状态标签
  const getStatusChip = (status) => {
    const statusConfig = {
      pending_review: { label: 'Pending Review', color: 'warning' },
      in_review: { label: 'In Review', color: 'info' },
      approved: { label: 'Approved', color: 'success' },
      rejected: { label: 'Rejected', color: 'error' }
    };

    const config = statusConfig[status];
    return (
      <Chip 
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
        mb: 4
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Home
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          Policy Review
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Review and process insurance policy applications
        </Typography>
      </Box>

      {/* 复杂度说明卡片 - 修改为可折叠 */}
      <Paper elevation={0} sx={{ 
        p: 2, 
        mb: 4, 
        border: '1px solid #e0e0e0',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: '#f8f9fa'
        }
      }}>
        <Box 
          onClick={() => setIsGuideExpanded(!isGuideExpanded)}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <IconButton size="small" sx={{ mr: 1 }}>
            <ExpandMoreIcon sx={{ 
              transform: isGuideExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s'
            }}/>
          </IconButton>
          <Typography variant="h6" sx={{ color: '#1976d2' }}>
            Case Complexity Classification
          </Typography>
        </Box>
        
        <Collapse in={isGuideExpanded}>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* Simple Cases */}
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2,
                borderLeft: '4px solid #4caf50',
                bgcolor: '#fff',
                borderRadius: 1,
                boxShadow: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip 
                    label="Simple" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                    sx={{ mr: 1.5 }}
                  />
                  <Typography variant="subtitle2">Low Risk Profile</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  • No claims in 6+ years
                  <br />
                  • Clean driving record
                  <br />
                  • Local jurisdiction
                </Typography>
              </Box>
            </Grid>

            {/* Moderate Cases */}
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2,
                borderLeft: '4px solid #ffb74d',
                bgcolor: '#fff',
                borderRadius: 1,
                boxShadow: 1 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip 
                    label="Moderate" 
                    size="small" 
                    color="warning" 
                    variant="outlined"
                    sx={{ mr: 1.5 }}
                  />
                  <Typography variant="subtitle2">Standard Risk</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  • Minor infractions
                  <br />
                  • Single claim history
                  <br />
                  • Standard documents
                </Typography>
              </Box>
            </Grid>

            {/* Complex Cases */}
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2,
                borderLeft: '4px solid #f44336',
                bgcolor: '#fff',
                borderRadius: 1,
                boxShadow: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip 
                    label="Complex" 
                    size="small" 
                    color="error" 
                    variant="outlined"
                    sx={{ mr: 1.5 }}
                  />
                  <Typography variant="subtitle2">High Complexity</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  • Multiple claims
                  <br />
                  • Cross-border exposure
                  <br />
                  • LOE required
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* Pending Review Policies Table */}
      <TableContainer component={Paper} elevation={2} sx={{ mb: 6 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Policy Number</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Submitted Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Case Complexity</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {policies.map((policy) => (
              <TableRow 
                key={policy.id}
                onClick={() => navigate(`/underwriter/policy-review/${policy.id}`)}
                sx={{ 
                  '&:hover': { 
                    bgcolor: '#f8f9fa',
                    cursor: 'pointer' 
                  },
                  transition: 'background-color 0.2s'
                }}
              >
                <TableCell>{policy.policyNumber}</TableCell>
                <TableCell>{policy.type}</TableCell>
                <TableCell>{policy.submittedBy}</TableCell>
                <TableCell>{policy.submittedDate}</TableCell>
                <TableCell>{getComplexityChip(policy.complexity)}</TableCell>
                <TableCell>{getStatusChip(policy.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default PolicyReviewPage; 